(() => {
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const zoomBounds = (width, height, ratio, scale) => {
    const imageWidth = Math.min(width, height * ratio);
    const imageHeight = Math.min(height, width / ratio);
    return { x: Math.max(0, imageWidth * scale - width) / (2 * scale), y: Math.max(0, imageHeight * scale - height) / (2 * scale) };
  };
  const externalVideo = source => {
    try {
      const url = new URL(source, location.origin);
      const host = url.hostname.toLowerCase();
      if (['youtube.com', 'www.youtube.com', 'youtube-nocookie.com', 'www.youtube-nocookie.com', 'youtu.be'].includes(host)) {
        const id = host === 'youtu.be' ? url.pathname.split('/')[1] : url.searchParams.get('v') || url.pathname.match(/\/(?:embed|shorts)\/([^/]+)/)?.[1];
        if (/^[\w-]{11}$/.test(id || '')) return { type: 'youtube', id, url: 'https://www.youtube.com/watch?v=' + id };
      }
      if (['vimeo.com', 'www.vimeo.com', 'player.vimeo.com'].includes(host)) {
        const id = url.pathname.match(/(?:\/video)?\/(\d+)/)?.[1];
        if (id) return { type: 'vimeo', id, url: 'https://vimeo.com/' + id };
      }
    } catch {}
    return null;
  };
  const icon = name => {
    const paths = {
      close: 'M6 6l12 12M18 6L6 18',
      previous: 'M15 5l-7 7 7 7',
      next: 'M9 5l7 7-7 7',
      zoom: 'M10 7v6M7 10h6M15 15l5 5',
      reset: 'M7 10h6M15 15l5 5',
    };
    return '<svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">' +
      (name === 'zoom' || name === 'reset' ? '<circle cx="10" cy="10" r="7"/>' : '') +
      '<path d="' + paths[name] + '"/></svg>';
  };

  class LuminoGallery {
    constructor(gallery) {
      this.gallery = gallery;
      this.product = gallery.closest('[data-js-product-component]');
      this.controller = new AbortController();
      this.galleryIndex = Number(gallery.querySelector('css-slider')?.index) || 0;
      this.viewportWidth = window.innerWidth;
      const { signal } = this.controller;
      gallery.addEventListener('click', event => {
        const media = event.target.closest('[data-product-media-type]');
        if (media?.dataset.productMediaType.includes('video') && !this.dragged) {
          event.preventDefault(); event.stopImmediatePropagation(); this.open(media);
        }
      }, { signal, capture: true });
      gallery.addEventListener('click', event => {
        const media = event.target.closest('[data-image-zoom]');
        if (media && !this.dragged) this.open(media);
      }, { signal });
      gallery.addEventListener('pointerdown', event => { this.startX = event.clientX; this.startY = event.clientY; this.dragged = false; }, { signal });
      gallery.addEventListener('pointermove', event => {
        if (event.buttons && Math.hypot(event.clientX - this.startX, event.clientY - this.startY) > 10) this.dragged = true;
        this.hover(event);
      }, { signal });
      gallery.addEventListener('pointerleave', () => this.hideHover(), { signal });
      gallery.addEventListener('keydown', event => {
        if (event.target.matches('[data-image-zoom]') && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault(); this.open(event.target);
        }
      }, { signal });
      gallery.addEventListener('change', event => {
        this.hideHover();
        if (!this.resizing && event.target.matches('css-slider')) this.galleryIndex = Number(event.target.index);
      }, { signal, capture: true });
      this.product?.addEventListener('VARIANT_CHANGE', () => this.updateProductDetails(), { signal, capture: true });
      window.addEventListener('scroll', () => this.hideHover(), { signal, passive: true, capture: true });
      window.addEventListener('resize', () => {
        this.hideHover();
        if (this.viewportWidth !== window.innerWidth) this.resizing = true;
        this.viewportWidth = window.innerWidth;
        if (!this.resizing && !this.dialog?.open) return;
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => {
          if (this.resizing) {
            const slider = gallery.querySelector('css-slider');
            if (slider?.sliderEnabled) slider.changeSlide(this.galleryIndex, 'auto');
            gallery.closest('product-page')?.thumbnailNavigationHelper(this.galleryIndex);
            this.resizing = false;
          }
          if (this.dialog?.open && this.mode === 'photos') {
            this.images.scrollTo({ left: this.images.children[this.selected].offsetLeft, top: 0, behavior: 'instant' });
            this.applyZoom();
          }
        }, 150);
      }, { signal });
      document.addEventListener('shopify:section:unload', event => {
        if (event.target.contains(gallery)) this.destroy();
      }, { signal });
    }

    collectVideos() {
      const videos = [];
      const add = video => {
        const key = video.type + ':' + (video.id || video.sources?.[0]?.src || '');
        if (!videos.some(item => item.key === key)) videos.push({ ...video, key });
      };
      this.gallery.querySelectorAll('video-component').forEach(component => {
        const template = component.querySelector('template');
        const frame = template?.content.querySelector('iframe');
        const file = template?.content.querySelector('video');
        const media = component.closest('[data-product-media-type]');
        const title = frame?.title || media?.querySelector('img')?.alt || this.gallery.dataset.videosLabel;
        const poster = media?.dataset.luminoVideoPoster || file?.poster || '';
        const external = frame && externalVideo(frame.getAttribute('src'));
        if (external) add({ ...external, title, poster, media });
        else if (file) {
          const sources = [...file.querySelectorAll('source')].map(source => ({ src: source.src, type: source.type }));
          if (sources.length) add({ type: 'file', sources, title, poster, media });
        }
      });
      this.product?.querySelectorAll('.lumino-product-details iframe[src], .lumino-product-details iframe[data-src], .lumino-product-details a[href]').forEach(element => {
        const video = externalVideo(element.getAttribute('src') || element.dataset.src || element.getAttribute('href'));
        if (video) add({ ...video, title: element.title || this.gallery.dataset.videosLabel, poster: '' });
      });
      return videos;
    }

    buildDialog() {
      this.photos = [...this.gallery.querySelectorAll('[data-image-zoom]')].filter(media => media.querySelector('[data-lumino-zoom-image]'));
      this.videos = this.collectVideos();
      if (!this.photos.length && !this.videos.length) return;
      const uid = 'lumino-gallery-' + this.product.id;
      this.dialog = document.createElement('dialog');
      this.dialog.className = 'lumino-gallery-dialog';
      this.dialog.setAttribute('aria-label', this.gallery.dataset.zoomLabel);
      this.dialog.innerHTML =
        '<header class="lumino-gallery-dialog__header"><div class="lumino-gallery-dialog__tabs" role="tablist"></div><button type="button" class="lumino-gallery-dialog__close">' + icon('close') + '</button></header>' +
        '<div class="lumino-gallery-dialog__layout"><div class="lumino-gallery-dialog__media">' +
        '<section class="lumino-gallery-dialog__photos" role="tabpanel"><div class="lumino-gallery-dialog__stage"><div class="lumino-gallery-dialog__images"></div>' +
        '<button type="button" class="lumino-gallery-dialog__previous">' + icon('previous') + '</button><button type="button" class="lumino-gallery-dialog__next">' + icon('next') + '</button>' +
        '<button type="button" class="lumino-gallery-dialog__zoom">' + icon('zoom') + '</button><span class="lumino-gallery-dialog__counter" aria-live="polite"></span></div><div class="lumino-gallery-dialog__thumbs"></div></section>' +
        '</div><aside class="lumino-gallery-dialog__sidebar"><h2 class="lumino-gallery-dialog__title"></h2><p class="lumino-gallery-dialog__sku"></p><div class="lumino-gallery-dialog__properties"></div><div class="lumino-gallery-dialog__purchase"></div></aside></div>';
      const find = selector => this.dialog.querySelector('.lumino-gallery-dialog__' + selector);
      this.closeButton = find('close');
      this.closeButton.setAttribute('aria-label', this.gallery.dataset.closeLabel);
      this.closeButton.addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      });
      this.tabs = find('tabs');
      this.tabs.setAttribute('aria-label', this.gallery.dataset.zoomLabel);
      this.photoPanel = find('photos');
      this.photoPanel.id = uid + '-photos';
      this.photoTab = this.buildTab('photos', this.gallery.dataset.photosLabel, this.photos.length, this.photoPanel);
      this.images = find('images');
      this.thumbs = find('thumbs');
      this.counter = find('counter');
      this.zoomButton = find('zoom');
      this.zoomButton.addEventListener('click', () => this.toggleZoom());
      this.previousButton = find('previous');
      this.nextButton = find('next');
      this.previousButton.setAttribute('aria-label', this.gallery.dataset.previousLabel);
      this.nextButton.setAttribute('aria-label', this.gallery.dataset.nextLabel);
      this.previousButton.addEventListener('click', () => this.select(this.selected - 1));
      this.nextButton.addEventListener('click', () => this.select(this.selected + 1));
      this.photos.forEach((media, index) => {
        const original = media.querySelector('img');
        const item = document.createElement('div');
        item.className = 'lumino-gallery-dialog__image';
        item.dataset.index = index;
        item.setAttribute('role', 'button');
        item.tabIndex = 0;
        item.setAttribute('aria-label', this.gallery.dataset.zoomLabel);
        const image = document.createElement('img');
        image.alt = original?.alt || '';
        image.width = Number(original?.getAttribute('width')) || 1000;
        image.height = Number(original?.getAttribute('height')) || 1000;
        image.src = original?.currentSrc || original?.src || media.querySelector('[data-lumino-zoom-image]').dataset.image;
        image.dataset.highResolution = media.querySelector('[data-lumino-zoom-image]').dataset.image;
        image.loading = 'lazy'; image.draggable = false;
        image.addEventListener('load', () => { if (index === this.selected) this.applyZoom(); });
        item.append(image);
        item.addEventListener('click', () => { if (!this.mobile()) this.toggleZoom(); });
        item.addEventListener('keydown', event => {
          if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); this.toggleZoom(); }
        });
        item.addEventListener('pointermove', event => this.panMouse(event, item));
        this.images.append(item);
        const thumb = document.createElement('button');
        thumb.type = 'button';
        thumb.setAttribute('aria-label', (index + 1) + '. ' + (image.alt || this.gallery.dataset.photosLabel));
        thumb.setAttribute('aria-current', 'false');
        const preview = document.createElement('img');
        preview.src = original?.currentSrc || original?.src || image.src;
        preview.alt = ''; preview.loading = 'lazy';
        thumb.append(preview);
        thumb.addEventListener('click', () => this.select(index));
        this.thumbs.append(thumb);
      });
      this.thumbs.hidden = this.photos.length < 2;
      this.previousButton.hidden = this.nextButton.hidden = this.photos.length < 2;
      this.videoPanel = null;
      if (this.videos.length) {
        this.videoPanel = document.createElement('section');
        this.videoPanel.className = 'lumino-gallery-dialog__videos';
        this.videoPanel.id = uid + '-videos';
        this.videoPanel.setAttribute('role', 'tabpanel');
        this.videoPanel.hidden = true;
        this.videoPanel.innerHTML = '<div class="lumino-gallery-dialog__video-stage"></div><div class="lumino-gallery-dialog__video-caption"><span></span><a target="_blank" rel="noopener"></a></div><div class="lumino-gallery-dialog__video-thumbs"></div>';
        find('media').append(this.videoPanel);
        this.videoTab = this.buildTab('videos', this.gallery.dataset.videosLabel, this.videos.length, this.videoPanel);
        this.videoStage = find('video-stage');
        this.videoThumbs = find('video-thumbs');
        this.videos.forEach((video, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.setAttribute('aria-label', (index + 1) + '. ' + video.title);
          button.setAttribute('aria-current', 'false');
          const preview = document.createElement('span');
          preview.className = 'lumino-gallery-dialog__video-preview';
          if (video.poster) { const image = document.createElement('img'); image.src = video.poster; image.alt = ''; image.loading = 'lazy'; preview.append(image); }
          const play = document.createElement('span'); play.textContent = '▶'; play.setAttribute('aria-hidden', 'true'); preview.append(play);
          const title = document.createElement('span'); title.textContent = video.title;
          button.append(preview, title);
          button.addEventListener('click', () => this.selectVideo(index));
          this.videoThumbs.append(button);
        });
        this.videoThumbs.hidden = this.videos.length < 2;
      }
      this.photoTab.hidden = !this.photos.length;
      this.tabs.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        const buttons = [...this.tabs.children].filter(button => !button.hidden);
        const current = buttons.indexOf(document.activeElement);
        const index = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (current + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length;
        event.preventDefault(); buttons[index].click(); buttons[index].focus();
      });
      this.dialog.addEventListener('close', () => {
        this.stopVideo(); this.restorePurchase();
        document.body.style.overflow = this.oldOverflow;
        this.resetZoom();
        const selected = this.photos[this.selected];
        const target = selected && !selected.inert ? selected : this.gallery.querySelector('.css-slide-active[data-product-media-type]') || this.originMedia;
        target?.focus({ preventScroll: true });
      });
      this.dialog.addEventListener('keydown', event => {
        if (event.target.closest('input, select, textarea, .lumino-purchase-panel, [role="tab"]')) return;
        const delta = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
        if (delta && this.mode === 'photos') { event.preventDefault(); this.select(this.selected + delta); }
      });
      this.images.addEventListener('touchstart', event => this.touchStart(event), { passive: false });
      this.images.addEventListener('touchmove', event => this.touchMove(event), { passive: false });
      this.images.addEventListener('touchend', () => this.touchEnd());
      this.images.addEventListener('touchcancel', () => { this.gesture = null; });
      this.product.append(this.dialog);
      this.updateProductDetails();
    }

    buildTab(mode, label, count, panel) {
      const button = document.createElement('button');
      button.type = 'button'; button.id = panel.id + '-tab';
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-controls', panel.id);
      button.setAttribute('aria-selected', 'false');
      button.tabIndex = -1;
      button.append(document.createTextNode(label + ' '));
      const badge = document.createElement('span'); badge.textContent = count; button.append(badge);
      panel.setAttribute('aria-labelledby', button.id);
      button.addEventListener('click', () => this.setMode(mode));
      this.tabs.append(button);
      return button;
    }

    updateProductDetails() {
      if (!this.dialog) return;
      this.dialog.querySelector('.lumino-gallery-dialog__title').textContent = this.product.querySelector('.lumino-product-summary .product__title')?.textContent || '';
      const sku = this.dialog.querySelector('.lumino-gallery-dialog__sku');
      sku.textContent = this.product.querySelector('[data-update-block="sku-barcode"] .product__sku')?.textContent || '';
      sku.hidden = !sku.textContent.trim();
    }

    mountPurchase() {
      this.restorePurchase();
      this.movedNodes = [];
      const move = (node, holder) => {
        if (!node || !holder) return;
        const anchor = document.createComment('lumino-gallery-return');
        node.before(anchor); holder.append(node);
        this.movedNodes.push({ node, anchor });
      };
      move(this.product.querySelector('.lumino-purchase-panel'), this.dialog.querySelector('.lumino-gallery-dialog__purchase'));
      this.product.querySelectorAll('[data-js-custom-property]').forEach(node => {
        if (node.querySelector('[required]') && !this.dialog.contains(node)) move(node, this.dialog.querySelector('.lumino-gallery-dialog__properties'));
      });
    }

    restorePurchase() {
      this.movedNodes?.forEach(({ node, anchor }) => { anchor.replaceWith(node); });
      this.movedNodes = [];
    }

    mobile() { return window.matchMedia('(max-width: 749px)').matches; }
    resetZoom() { this.scale = 1; this.x = 0; this.y = 0; this.manualZoom = false; this.lastTap = null; this.gesture = null; }

    open(media) {
      if (!this.dialog) this.buildDialog();
      if (!this.dialog || this.dialog.open) return;
      this.originMedia = media;
      this.hideHover(); this.resetZoom(); this.updateProductDetails(); this.mountPurchase();
      this.oldOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.dialog.showModal();
      const photoIndex = this.photos.indexOf(media);
      const videoIndex = this.videos.findIndex(video => video.media === media);
      if (videoIndex >= 0 || !this.photos.length) { this.selectedVideo = Math.max(0, videoIndex); this.setMode('videos'); }
      else { this.selected = Math.max(0, photoIndex); this.setMode('photos'); }
      this.closeButton.focus({ preventScroll: true });
    }

    setMode(mode) {
      if (mode === 'videos' && !this.videos.length) return;
      this.mode = mode;
      this.photoPanel.hidden = mode !== 'photos';
      if (this.videoPanel) this.videoPanel.hidden = mode !== 'videos';
      [this.photoTab, this.videoTab].filter(Boolean).forEach(button => {
        const selected = button === (mode === 'photos' ? this.photoTab : this.videoTab);
        button.setAttribute('aria-selected', String(selected)); button.tabIndex = selected ? 0 : -1;
      });
      if (mode === 'videos') this.selectVideo(this.selectedVideo || 0);
      else { this.stopVideo(); this.select(this.selected || 0, false); }
    }

    select(index, animate = true) {
      if (!this.photos.length) return;
      index = clamp(index, 0, this.photos.length - 1);
      this.resetZoom(); this.markSelected(index); this.applyZoom();
      const item = this.images.children[index];
      const behavior = animate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'instant';
      this.images.scrollTo({ left: item.offsetLeft, top: 0, behavior });
    }

    markSelected(index) {
      this.selected = index;
      const image = this.images.children[index].querySelector('img');
      image.loading = 'eager';
      if (!image.dataset.preloaded) {
        image.dataset.preloaded = 'true';
        const highResolution = new Image();
        highResolution.onload = () => { image.src = highResolution.src; };
        highResolution.src = image.dataset.highResolution;
      }
      [...this.images.children].forEach((item, i) => { item.inert = i !== index; });
      [...this.thumbs.children].forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)));
      const thumb = this.thumbs.children[index];
      this.thumbs.scrollTo({ left: clamp(thumb.offsetLeft - this.thumbs.clientWidth / 2 + thumb.offsetWidth / 2, 0, this.thumbs.scrollWidth), behavior: 'instant' });
      this.counter.textContent = (index + 1) + ' / ' + this.photos.length;
      this.previousButton.disabled = index === 0;
      this.nextButton.disabled = index === this.photos.length - 1;
      const mediaIndex = Number(this.photos[index].dataset.index);
      const slider = this.gallery.querySelector('css-slider');
      if (slider?.sliderEnabled && Number(slider.index) !== mediaIndex) slider.changeSlide(mediaIndex, 'auto');
    }

    selectVideo(index) {
      if (!this.videos.length || !this.videoStage) return;
      this.stopVideo(); this.selectedVideo = index;
      const video = this.videos[index];
      let player;
      if (video.type === 'file') {
        player = document.createElement('video');
        player.controls = true; player.playsInline = true; player.preload = 'metadata';
        if (video.poster) player.poster = video.poster;
        video.sources.forEach(source => { const node = document.createElement('source'); node.src = source.src; node.type = source.type; player.append(node); });
      } else {
        player = document.createElement('iframe');
        player.src = video.type === 'youtube' ? 'https://www.youtube.com/embed/' + encodeURIComponent(video.id) + '?rel=0&playsinline=1' : 'https://player.vimeo.com/video/' + encodeURIComponent(video.id) + '?dnt=1&playsinline=1';
        player.title = video.title;
        player.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
        player.allowFullscreen = true;
        player.referrerPolicy = 'strict-origin-when-cross-origin';
      }
      this.videoStage.append(player);
      const caption = this.dialog.querySelector('.lumino-gallery-dialog__video-caption');
      caption.querySelector('span').textContent = video.title;
      const link = caption.querySelector('a');
      link.textContent = this.gallery.dataset.openVideoLabel;
      link.href = video.url || video.sources?.[0]?.src || '#';
      [...this.videoThumbs.children].forEach((button, i) => button.setAttribute('aria-current', String(i === index)));
    }

    stopVideo() {
      this.videoStage?.querySelector('video')?.pause();
      this.videoStage?.replaceChildren();
    }

    applyZoom() {
      if (!this.images || this.selected == null) return;
      const item = this.images.children[this.selected], image = item.querySelector('img');
      const bounds = zoomBounds(item.clientWidth, item.clientHeight, image.naturalWidth / image.naturalHeight || 1, this.scale || 1);
      this.x = clamp(this.x || 0, -bounds.x, bounds.x);
      this.y = clamp(this.y || 0, -bounds.y, bounds.y);
      [...this.images.children].forEach(element => {
        const active = element === item;
        element.style.setProperty('--zoom-scale', active ? this.scale : 1);
        element.style.setProperty('--zoom-x', (active ? this.x : 0) + 'px');
        element.style.setProperty('--zoom-y', (active ? this.y : 0) + 'px');
        element.classList.toggle('is-zoomed', active && this.scale > 1);
      });
      if (this.zoomButton) {
        this.zoomButton.innerHTML = icon(this.scale > 1 ? 'reset' : 'zoom');
        this.zoomButton.setAttribute('aria-label', this.scale > 1 ? this.gallery.dataset.zoomOutLabel : this.gallery.dataset.zoomInLabel);
        this.zoomButton.setAttribute('aria-pressed', String(this.scale > 1));
      }
    }

    toggleZoom() {
      this.scale = this.scale > 1 ? 1 : 2;
      this.x = 0; this.y = 0; this.lastTap = null; this.manualZoom = false; this.applyZoom();
    }

    panMouse(event, item) {
      if (event.pointerType !== 'mouse' || event.buttons || this.scale <= 1 || Number(item.dataset.index) !== this.selected) return;
      const rect = item.getBoundingClientRect(), image = item.querySelector('img');
      const bounds = zoomBounds(rect.width, rect.height, image.naturalWidth / image.naturalHeight || 1, this.scale);
      this.x = (0.5 - clamp((event.clientX - rect.left) / rect.width, 0, 1)) * 2 * bounds.x;
      this.y = (0.5 - clamp((event.clientY - rect.top) / rect.height, 0, 1)) * 2 * bounds.y;
      this.applyZoom();
    }

    touchStart(event) {
      if (!this.mobile()) return;
      event.preventDefault();
      const [first, second] = event.touches;
      if (second) {
        this.gesture = { distance: Math.hypot(first.clientX - second.clientX, first.clientY - second.clientY), scale: this.scale };
        this.lastTap = null;
      } else {
        const now = Date.now();
        if (this.lastTap && now - this.lastTap.time < 300 && Math.hypot(first.clientX - this.lastTap.x, first.clientY - this.lastTap.y) < 50) {
          this.toggleZoom(); this.gesture = null;
        } else {
          this.lastTap = { time: now, x: first.clientX, y: first.clientY };
          this.gesture = { startX: first.clientX, startY: first.clientY, x: this.x, y: this.y, dx: 0, dy: 0 };
        }
      }
    }

    touchMove(event) {
      if (!this.mobile() || !this.gesture) return;
      event.preventDefault();
      const [first, second] = event.touches;
      if (second && this.gesture.distance) {
        const previous = this.scale;
        this.scale = clamp(Math.hypot(first.clientX - second.clientX, first.clientY - second.clientY) / this.gesture.distance * this.gesture.scale, 1, 5);
        const rect = this.images.children[this.selected].getBoundingClientRect();
        this.x -= (((first.clientX + second.clientX) / 2 - rect.left - rect.width / 2) * (this.scale / previous - 1)) / this.scale;
        this.y -= (((first.clientY + second.clientY) / 2 - rect.top - rect.height / 2) * (this.scale / previous - 1)) / this.scale;
        this.manualZoom = true;
      } else if (!second && this.gesture.startX != null) {
        const dx = first.clientX - this.gesture.startX, dy = first.clientY - this.gesture.startY;
        this.gesture.dx = dx; this.gesture.dy = dy;
        if (Math.hypot(dx, dy) < 10) return;
        this.lastTap = null;
        this.x = this.gesture.x + dx / this.scale;
        this.y = this.gesture.y + dy / this.scale;
      }
      this.applyZoom();
    }

    touchEnd() {
      const gesture = this.gesture;
      this.gesture = null;
      if (this.scale === 1 && gesture?.dx && Math.abs(gesture.dx) > 50 && Math.abs(gesture.dx) > Math.abs(gesture.dy) * 1.25) this.select(this.selected + (gesture.dx < 0 ? 1 : -1));
    }

    hover(event) {
      if (event.pointerType !== 'mouse' || event.buttons || !window.matchMedia('(min-width: 1200px) and (hover: hover) and (pointer: fine)').matches) return;
      const media = event.target.closest('[data-image-zoom]');
      if (!media || (this.dialog?.open)) { this.hideHover(); return; }
      const rect = media.getBoundingClientRect();
      const left = this.gallery.getBoundingClientRect().right + 24;
      const purchase = this.gallery.closest('.main-product')?.querySelector('.lumino-purchase-panel');
      const right = Math.min(window.innerWidth - 24, purchase ? purchase.getBoundingClientRect().left - 16 : Infinity);
      const width = right - left;
      const height = Math.min(rect.height, window.innerHeight - rect.top - 16);
      if (width < 260 || height < 220) { this.hideHover(); return; }
      if (this.hoverMedia !== media) this.hideHover();
      this.hoverMedia = media;
      if (!this.pane) {
        this.pane = document.createElement('div');
        this.pane.className = 'lumino-product-hover-zoom';
        this.pane.setAttribute('aria-hidden', 'true');
        document.body.append(this.pane);
      }
      const scale = Math.max(3.5, width / rect.width + .35, height / rect.height + .35);
      const lensWidth = Math.min(rect.width, width / scale), lensHeight = Math.min(rect.height, height / scale);
      const lensLeft = clamp(event.clientX - rect.left - lensWidth / 2, 0, rect.width - lensWidth);
      const lensTop = clamp(event.clientY - rect.top - lensHeight / 2, 0, rect.height - lensHeight);
      media.style.setProperty('--lens-width', `${lensWidth}px`);
      media.style.setProperty('--lens-height', `${lensHeight}px`);
      media.style.setProperty('--lens-left', `${lensLeft}px`);
      media.style.setProperty('--lens-top', `${lensTop}px`);
      media.classList.add('is-hover-zooming');
      Object.assign(this.pane.style, { left: `${left}px`, top: `${rect.top}px`, width: `${width}px`, height: `${height}px`, backgroundImage: `url(${JSON.stringify(media.querySelector('[data-lumino-zoom-image]').dataset.image)})`, backgroundPosition: `${-lensLeft * scale}px ${-lensTop * scale}px`, backgroundSize: `${rect.width * scale}px ${rect.height * scale}px` });
      this.pane.classList.add('is-visible');
    }
    hideHover() { this.hoverMedia?.classList.remove('is-hover-zooming'); this.hoverMedia = null; this.pane?.classList.remove('is-visible'); }
    destroy() { if (this.dialog?.open) this.dialog.close(); clearTimeout(this.resizeTimer); this.controller.abort(); this.restorePurchase(); this.stopVideo(); this.dialog?.remove(); this.pane?.remove(); }
  }
  const initialize = (root = document) => root.querySelectorAll('.main-product .lumino-product-gallery').forEach(gallery => {
    if (!gallery.luminoGallery) gallery.luminoGallery = new LuminoGallery(gallery);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
})();
